import { supabase } from '@/lib/supabase';
import { useCallback, useEffect, useState } from 'react';

export interface UserProfile {
  id: string;
  full_name: string | null;
  diploma: string | null;
  section: string | null;
  subject_codes: string[] | null;
  premium: boolean;
  subscription_source: 'apple' | 'google' | 'stripe' | null;
  xp: number;
  streak_days: number;
  best_streak: number;
  last_activity_date: string | null;
  onboarding_completed: boolean;
  push_token: string | null;
  notifications_enabled: boolean;
}

export function useUserProfile() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setProfile(null);
        setLoading(false);
        return;
      }

      const { data, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
        throw profileError;
      }

      setProfile(data as UserProfile);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();

    // ✅ Écouter les changements du profil en temps réel
    let channel: any = null;

    const setupRealtimeSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      channel = supabase
        .channel('profile-changes')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'profiles',
            filter: `id=eq.${user.id}`,
          },
          (payload) => {
            console.log('✅ Profile updated in realtime:', payload.new);
            setProfile(payload.new as UserProfile);
          }
        )
        .subscribe();
    };

    setupRealtimeSubscription();

    // ✅ Écouter aussi les changements d'auth (connexion/déconnexion)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          fetchProfile();
        } else if (event === 'SIGNED_OUT') {
          setProfile(null);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [fetchProfile]);

  const refetch = useCallback(() => {
    setLoading(true);
    fetchProfile();
  }, [fetchProfile]);

  return {
    profile,
    loading,
    error,
    refetch,
    // Helpers pratiques
    isPremium: profile?.premium || false,
    isOnboardingCompleted: profile?.onboarding_completed || false,
  };
}
